/**
 * Словарь каталога Jamendo, снятый с выборки из 800 треков (2026-09-10):
 * order = popularity_total, buzzrate, releasedate, listens_month, downloads_total.
 *
 * Числа — сколько раз тег встретился в выборке. Они нужны не для справки:
 * ранжирование взвешивает совпадение по редкости тега. Совпасть по `neutral`
 * (186 из 800) не значит ничего; совпасть по `melancholic` (10 из 800) значит много.
 *
 * Метод важнее самих чисел: у оригинала правила ссылались на теги, которых в
 * каталоге почти нет — `minor` ~10 треков на весь каталог, `major` 0. Выборка
 * заменяет угадывание.
 */
export const TAG_FREQ: Record<string, number> = {
  // жанры
  pop: 238, electronic: 109, rock: 106, dance: 53, classical: 49, newage: 43,
  indie: 43, ambient: 35, folk: 30, hiphop: 30, jazz: 30, electropop: 26,
  filmscore: 25, corporate: 20, house: 19, rnb: 19, edm: 18, lofi: 18,
  metal: 17, chillout: 15, soul: 14, singersongwriter: 14, synthpop: 14,
  choral: 12, poprock: 11, chillhop: 11, dreampop: 9, punk: 9, downtempo: 8,
  blues: 12, reggae: 10, latin: 10, ska: 6, drumnbass: 8, experimental: 12,
  orchestral: 14, relaxation: 20, meditative: 8, bluesrock: 6,
  // инструменты
  synthesizer: 129, piano: 114, strings: 96, guitar: 67, drums: 59,
  electricguitar: 52, bass: 29, orchestra: 16, acousticguitar: 9,
  percussion: 8, flute: 7, violin: 6, saxophone: 5, pipeorgan: 5,
  cello: 2, classicalguitar: 2, ukulele: 2, harmonica: 1,
  // настроения (vartags)
  acoustic: 402, voice: 401, vocal: 287, neutral: 186, energetic: 164,
  happy: 78, peaceful: 69, love: 57, instrumental: 53, emotional: 29,
  soft: 27, romantic: 17, upbeat: 15, party: 15, sad: 14, lounge: 14,
  relaxing: 12, dark: 12, fast: 12, groovy: 11, melancholic: 10,
  adventure: 9, cinematic: 8, sentimental: 8, summer: 7, calm: 9,
  nostalgic: 6, uplifting: 8,
};

/** Тег существует в каталоге. Правило на отсутствующем теге не срабатывает никогда. */
export const VERIFIED_TAGS = new Set(Object.keys(TAG_FREQ));

const SAMPLE = 800;

/**
 * Вес совпадения по редкости: частый тег почти ничего не сообщает,
 * редкий сообщает много. Ограничен сверху, чтобы единичный тег не решал всё.
 */
export function rarity(tag: string): number {
  const f = TAG_FREQ[tag];
  if (!f) return 1;
  return Math.min(3, Math.log(SAMPLE / f) / Math.log(8));
}

/**
 * Распределение темпов в каталоге, та же выборка:
 *   medium 611 (76%) · high 100 (12%) · low 37 (4.6%) · verylow 16 (2%) · veryhigh 15 (1.9%)
 *
 * Отсюда правило: жёстко фильтровать по редкому темпу нельзя — пул схлопывается,
 * и все тихие дни начинают звучать одинаково. `low` и `verylow` уходят в
 * ранжирование, `medium` и `high` можно ставить фильтром.
 */
export const SPEED_SHARE: Record<string, number> = {
  medium: 0.764, high: 0.125, low: 0.046, verylow: 0.02, veryhigh: 0.019,
};

export function speedIsFilterable(speed: string): boolean {
  return (SPEED_SHARE[speed] ?? 0) >= 0.1;
}

/**
 * Сколько тегов реально несёт трек: замер по каталогу 2026-09-13 —
 * медиана 3, минимум 3, максимум 8. Совпасть с десятком терминов профиля
 * трек не может физически, и нормировать оценку надо на это число.
 */
export const TYPICAL_TRACK_TAGS = 4;

/**
 * Родство тегов.
 *
 * Раньше совпадение искалось подстрокой: `x === tag || x.includes(tag) ||
 * tag.includes(x)`. Это протекало ровно там, где дороже всего. `acoustic`
 * стоит на 402 треках из 800 — и `"acousticguitar".includes("acoustic")`
 * отдавал половине каталога полный вес тега, который встречается 9 раз.
 * Самый ценный по редкости термин правила «прохладно» присуждался даром,
 * а взвешивание по редкости из этого же файла обесценивалось.
 *
 * Заодно ловилось и просто случайное: `pop` внутри `hiphop`, `strings`
 * внутри `string`.
 *
 * Пары ниже — родство по существу, а не по буквам. Коэффициент — какую долю
 * веса термина получает трек, попавший в соседа, а не в сам термин. Он всегда
 * меньше единицы: родня не должна выигрывать у точного совпадения.
 */
export const KIN_PAIRS: [string, string, number][] = [
  // — составной инструмент и его части —
  ["acousticguitar", "guitar", 0.5], ["acousticguitar", "acoustic", 0.3],
  ["electricguitar", "guitar", 0.5], ["electricguitar", "electric", 0.3],
  ["classicalguitar", "guitar", 0.5], ["classicalguitar", "classical", 0.3],
  ["orchestral", "orchestra", 0.8], ["orchestral", "strings", 0.5], ["orchestral", "classical", 0.6],
  ["classical", "orchestra", 0.5], ["strings", "violin", 0.6], ["strings", "cello", 0.6],
  ["piano", "classical", 0.25], ["choral", "voice", 0.3], ["classical", "choral", 0.5],

  // — жанровые соседи —
  ["rock", "poprock", 0.5], ["rock", "bluesrock", 0.5], ["rock", "punk", 0.4], ["rock", "metal", 0.35],
  ["pop", "electropop", 0.5], ["pop", "synthpop", 0.45], ["pop", "poprock", 0.5], ["pop", "dreampop", 0.4],
  ["electronic", "house", 0.5], ["electronic", "edm", 0.55], ["electronic", "dance", 0.5],
  ["electronic", "synthpop", 0.4], ["electronic", "downtempo", 0.4], ["electronic", "drumnbass", 0.45],
  ["house", "edm", 0.6], ["dance", "edm", 0.6], ["dance", "party", 0.4],
  ["chillout", "lounge", 0.6], ["chillout", "downtempo", 0.6], ["chillout", "chillhop", 0.5],
  ["chillout", "lofi", 0.5], ["chillout", "relaxation", 0.5], ["chillout", "ambient", 0.4],
  ["lofi", "chillhop", 0.7], ["hiphop", "chillhop", 0.5], ["hiphop", "rnb", 0.4],
  ["jazz", "blues", 0.4], ["jazz", "soul", 0.4], ["jazz", "saxophone", 0.4], ["jazz", "lounge", 0.35],
  ["blues", "bluesrock", 0.7], ["soul", "rnb", 0.5], ["reggae", "ska", 0.6], ["latin", "reggae", 0.3],
  ["ambient", "newage", 0.5], ["ambient", "meditative", 0.5], ["ambient", "experimental", 0.35],
  ["newage", "meditative", 0.5], ["newage", "relaxation", 0.5],
  ["filmscore", "cinematic", 0.8], ["filmscore", "orchestral", 0.6], ["cinematic", "orchestral", 0.5],
  ["folk", "singersongwriter", 0.5], ["folk", "acousticguitar", 0.4], ["indie", "singersongwriter", 0.35],
  ["indie", "dreampop", 0.4], ["metal", "punk", 0.4],

  // — настроения —
  ["melancholic", "sad", 0.6], ["melancholic", "nostalgic", 0.5], ["melancholic", "sentimental", 0.5],
  ["melancholic", "emotional", 0.45], ["sad", "emotional", 0.5], ["nostalgic", "sentimental", 0.6],
  ["happy", "upbeat", 0.6], ["happy", "uplifting", 0.55], ["upbeat", "energetic", 0.5],
  ["uplifting", "energetic", 0.4], ["happy", "party", 0.4], ["party", "groovy", 0.4],
  ["peaceful", "calm", 0.7], ["peaceful", "relaxing", 0.65], ["peaceful", "soft", 0.55],
  ["calm", "relaxing", 0.7], ["relaxing", "relaxation", 0.8], ["soft", "calm", 0.5],
  ["meditative", "calm", 0.5], ["dark", "experimental", 0.3], ["romantic", "love", 0.6],
  ["romantic", "sentimental", 0.45], ["summer", "happy", 0.3], ["fast", "energetic", 0.5],
  ["fast", "upbeat", 0.4], ["adventure", "cinematic", 0.4], ["vocal", "voice", 0.8],
];

const KIN = new Map<string, Map<string, number>>();
for (const [a, b, w] of KIN_PAIRS) {
  if (!KIN.has(a)) KIN.set(a, new Map());
  if (!KIN.has(b)) KIN.set(b, new Map());
  KIN.get(a)!.set(b, w);
  KIN.get(b)!.set(a, w);
}

/**
 * Насколько тег трека засчитывается за термин профиля: 1 — это он и есть,
 * от 0 до 1 — родня, 0 — мимо.
 */
export function tagAffinity(profileTag: string, trackTag: string): number {
  if (profileTag === trackTag) return 1;
  return KIN.get(profileTag)?.get(trackTag) ?? 0;
}

/** Слова, из которых тега не получается: они есть в любом названии. */
const STOP = new Set(["of", "the", "and", "for", "with", "feat", "vol", "mix", "solo", "music", "track"]);

/**
 * Теги трека к сравнимому виду.
 *
 * Архив хранит их в `subject` человеческой строкой: «classical; dark ambient;
 * piano solo». После точного сравнения такой тег не совпал бы ни с чем, хотя
 * внутри него есть ровно то, что нужно. Поэтому кроме самого тега отдаём и
 * его слова — но именно слова, а не любые подстроки.
 */
export function normalizeTags(raw: string[]): string[] {
  const out = new Set<string>();
  for (const item of raw) {
    const tag = item.trim().toLowerCase();
    if (!tag) continue;
    out.add(tag);
    if (!tag.includes(" ")) continue;
    for (const word of tag.split(/\s+/)) {
      if (word.length > 2 && !STOP.has(word)) out.add(word);
    }
  }
  return [...out];
}
